import { createContext, useContext, useId, useState } from 'react';
import { cn } from '@/lib/cn';

interface TabsCtx {
  value: string;
  setValue: (v: string) => void;
  baseId: string;
}
const TabsContext = createContext<TabsCtx | null>(null);

function useTabs(): TabsCtx {
  const ctx = useContext(TabsContext);
  if (!ctx) throw new Error('Tabs.* debe usarse dentro de <Tabs>');
  return ctx;
}

export interface TabsProps {
  defaultValue: string;
  value?: string;
  onValueChange?: (v: string) => void;
  children: React.ReactNode;
  className?: string;
}

export function Tabs({ defaultValue, value, onValueChange, children, className }: TabsProps) {
  const [internal, setInternal] = useState(defaultValue);
  const baseId = useId();
  const current = value ?? internal;
  const setValue = (v: string) => {
    setInternal(v);
    onValueChange?.(v);
  };
  return (
    <TabsContext.Provider value={{ value: current, setValue, baseId }}>
      <div className={className}>{children}</div>
    </TabsContext.Provider>
  );
}

export function TabsList({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <div
      role="tablist"
      className={cn(
        // 21-oct, macOS 27: control segmentado. Carril #E6E6E6 / #323232 de radio 7, el elegido lleno del acento.
        'inline-flex max-w-full items-center gap-0.5 overflow-x-auto rounded-[9px] bg-[var(--mac-carril)] p-0.5 lateral:rounded-[7px]',
        className,
      )}
    >
      {children}
    </div>
  );
}

export function TabsTrigger({ value, children }: { value: string; children: React.ReactNode }) {
  const { value: current, setValue } = useTabs();
  const active = current === value;
  return (
    <button
      role="tab"
      aria-selected={active}
      onClick={() => setValue(value)}
      className={cn(
        'min-h-11 shrink-0 whitespace-nowrap rounded-[7px] px-3 text-sm transition-colors lateral:min-h-5 lateral:rounded-[5px] lateral:px-2.5',
        active
          ? 'bg-acento text-white'
          : 'text-slate-900 hover:bg-black/5 dark:text-slate-100 dark:hover:bg-white/5',
      )}
    >
      {children}
    </button>
  );
}

export function TabsContent({ value, children, className }: { value: string; children: React.ReactNode; className?: string }) {
  const { value: current } = useTabs();
  if (current !== value) return null;
  return <div className={cn('mt-4', className)}>{children}</div>;
}
